const BASE_URL = 'https://api.openf1.org/v1';

// Generic cache helper for OpenF1 API calls
// TTL is 5 minutes to prevent spamming the API and avoid 429 Too Many Requests errors
const CACHE_TTL_MS = 5 * 60 * 1000;

const openF1Queue = [];
let isQueueProcessing = false;

const processQueue = async () => {
    if (isQueueProcessing) return;
    isQueueProcessing = true;
    while (openF1Queue.length > 0) {
        const { url, resolve, reject } = openF1Queue.shift();
        try {
            const res = await fetch(url);
            if (!res.ok) {
                if (res.status === 404) {
                    resolve([]); // Graceful fallback per 404
                } else {
                    throw new Error(`OpenF1 API Error: ${res.status}`);
                }
            } else {
                const data = await res.json();
                resolve(data);
            }
        } catch (error) {
            reject(error);
        }
        // Attesa di 350ms tra le richieste per evitare il ban (status 429)
        if (openF1Queue.length > 0) {
            await new Promise(r => setTimeout(r, 350));
        }
    }
    isQueueProcessing = false;
};

const fetchWithCache = async (url, cacheKey) => {
    try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
            const parsed = JSON.parse(cached);
            if (Date.now() - parsed.timestamp < CACHE_TTL_MS) {
                return parsed.data;
            }
        }
    } catch (e) {
        /* ignore */
    }

    const data = await new Promise((resolve, reject) => {
        openF1Queue.push({ url, resolve, reject });
        processQueue();
    });

    try {
        localStorage.setItem(cacheKey, JSON.stringify({ data, timestamp: Date.now() }));
    } catch (e) {
        /* ignore */
    }

    return data;
};

export const openF1Service = {
    /**
     * Finds a session_key for a specific race meeting.
     * OpenF1 often requires session_key for queries.
     */
    async getSessionKey(year, round, sessionName = 'Race') {
        try {
            // Match the round (Ergast and OpenF1 rounds usually align, or we can match meeting_name but round is safer)
            // OpenF1 doesn't have a direct 'round' concept always, let's just get the meeting for that year.
            // Actually, OpenF1 provides meetings with a 'meeting_key'. 
            // We can fetch sessions directly by year.
            const sessRes = await fetch(`${BASE_URL}/sessions?year=${year}&session_name=${sessionName}`);
            const sessions = await sessRes.json();

            // Assuming the sessions are ordered or we can match the country from Ergast if needed.
            // But let's just return the sessions and find by round index if needed.
            // For now, if round is provided, we sort by date and pick the n-th one.
            if (!sessions || sessions.length === 0) return null;
            sessions.sort((a, b) => new Date(a.date_start) - new Date(b.date_start));

            const targetSession = sessions[round - 1]; // round is 1-indexed
            return targetSession ? targetSession.session_key : null;
        } catch (error) {
            console.error('Error fetching OpenF1 session:', error);
            return null;
        }
    },

    /**
     * Gets the latest available race session key for a given year.
     */
    async getLatestSessionKey(year) {
        try {
            const sessRes = await fetch(`${BASE_URL}/sessions?year=${year}&session_name=Race`);
            const sessions = await sessRes.json();
            if (!sessions || sessions.length === 0) return null;
            sessions.sort((a, b) => new Date(a.date_start) - new Date(b.date_start));
            return sessions[sessions.length - 1].session_key;
        } catch (error) {
            console.error('Error fetching latest session:', error);
            return null;
        }
    },

    /**
     * Gets all drivers for a session with in-memory caching
     * to prevent redundant network requests when switching tabs.
     */
    async getDrivers(sessionKey) {
        const CACHE_KEY = `f1_cache_drivers_${sessionKey}`;
        try {
            const cached = localStorage.getItem(CACHE_KEY);
            if (cached) {
                const parsed = JSON.parse(cached);
                // Drivers list doesn't change much, 24 hours TTL
                if (Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
                    return parsed.data;
                }
            }
        } catch (e) {
            console.warn('Error reading driver cache', e);
        }

        try {
            const res = await fetch(`${BASE_URL}/drivers?session_key=${sessionKey}`);
            const data = await res.json();

            const unique = [];
            if (Array.isArray(data)) {
                data.forEach(d => {
                    if (!unique.find(u => u.driver_number === d.driver_number)) {
                        unique.push(d);
                    }
                });
                const sorted = unique.sort((a, b) => a.driver_number - b.driver_number);

                try {
                    localStorage.setItem(CACHE_KEY, JSON.stringify({ data: sorted, timestamp: Date.now() }));
                } catch (e) { console.warn('Error saving to driver cache', e); }

                return sorted;
            }
            return [];
        } catch (error) {
            try { return JSON.parse(localStorage.getItem(CACHE_KEY))?.data || []; } catch (e) { return []; }
        }
    },

    /**
     * Gets results for a Free Practice session sorting by fastest lap
     */
    async getPracticeResults(year, datePrefix, sessionName) {
        try {
            const sessRes = await fetch(`${BASE_URL}/sessions?year=${year}&session_name=${encodeURIComponent(sessionName)}`);
            const sessions = await sessRes.json();
            if (!sessions || sessions.length === 0) return null;

            const session = sessions.find(s => s.date_start.startsWith(datePrefix));
            if (!session) return null;

            const key = session.session_key;

            // Fetch drivers and laps concurrently
            const [drvRes, lapsRes] = await Promise.all([
                fetch(`${BASE_URL}/drivers?session_key=${key}`),
                fetch(`${BASE_URL}/laps?session_key=${key}`)
            ]);

            const [drv, laps] = await Promise.all([drvRes.json(), lapsRes.json()]);

            // Filter laps with a valid duration and map fastest lap to each driver
            const results = drv.map(d => {
                const driverLaps = laps.filter(l => l.driver_number === d.driver_number && l.lap_duration);
                const bestLap = driverLaps.length ? Math.min(...driverLaps.map(l => l.lap_duration)) : null;
                return { ...d, bestLap };
            }).filter(d => d.bestLap).sort((a, b) => a.bestLap - b.bestLap);

            return results;
        } catch (error) {
            console.error(`Error fetching practice results for ${sessionName}:`, error);
            return [];
        }
    },

    /**
     * Gets results for Sprint Qualifying session sorting by fastest lap
     */
    async getSprintQualiResults(year, datePrefix) {
        try {
            const sessRes = await fetch(`${BASE_URL}/sessions?year=${year}&session_name=${encodeURIComponent('Sprint Qualifying')}`);
            const sessions = await sessRes.json();
            if (!sessions || sessions.length === 0) return null;

            const session = sessions.find(s => s.date_start.startsWith(datePrefix));
            if (!session) return null;

            const key = session.session_key;

            // Fetch drivers and laps concurrently
            const [drvRes, lapsRes] = await Promise.all([
                fetch(`${BASE_URL}/drivers?session_key=${key}`),
                fetch(`${BASE_URL}/laps?session_key=${key}`)
            ]);

            const [drv, laps] = await Promise.all([drvRes.json(), lapsRes.json()]);

            const results = drv.map(d => {
                const driverLaps = laps.filter(l => l.driver_number === d.driver_number && l.lap_duration);
                const bestLap = driverLaps.length ? Math.min(...driverLaps.map(l => l.lap_duration)) : null;
                return { ...d, bestLap };
            }).filter(d => d.bestLap).sort((a, b) => a.bestLap - b.bestLap);

            // Add position property for consistency
            return results.map((r, index) => ({ ...r, position: (index + 1).toString() }));
        } catch (error) {
            console.error(`Error fetching sprint quali results for ${datePrefix}:`, error);
            return [];
        }
    },

    /**
     * 1. Analisi Telemetrica Avanzata (Car Data)
     * Fetches telemetry for a specific driver in a session.
     */
    async getCarData(sessionKey, driverNumber) {
        try {
            const cacheKey = `f1_cardata_${sessionKey}_${driverNumber}`;
            return await fetchWithCache(`${BASE_URL}/car_data?session_key=${sessionKey}&driver_number=${driverNumber}`, cacheKey);
        } catch (error) {
            try { return JSON.parse(localStorage.getItem(`f1_cardata_${sessionKey}_${driverNumber}`))?.data || []; } catch (e) { return []; }
        }
    },

    /**
     * 2. Radio Box Reali (Team Radio)
     * Fetches radio communications.
     */
    async getTeamRadio(sessionKey, driverNumber) {
        try {
            const url = driverNumber
                ? `${BASE_URL}/team_radio?session_key=${sessionKey}&driver_number=${driverNumber}`
                : `${BASE_URL}/team_radio?session_key=${sessionKey}`;
            const cacheKey = `f1_radio_${sessionKey}_${driverNumber || 'all'}`;
            return await fetchWithCache(url, cacheKey);
        } catch (error) {
            try { return JSON.parse(localStorage.getItem(`f1_radio_${sessionKey}_${driverNumber || 'all'}`))?.data || []; } catch (e) { return []; }
        }
    },

    /**
     * 3. Minimappa in Tempo Reale (Location)
     * Fetches position data for all cars in a session.
     */
    async getLocationData(sessionKey, driverNumber = null) {
        try {
            let url = `${BASE_URL}/location?session_key=${sessionKey}`;
            if (driverNumber) url += `&driver_number=${driverNumber}`;
            const cacheKey = `f1_loc_${sessionKey}_${driverNumber || 'all'}`;
            return await fetchWithCache(url, cacheKey);
        } catch (error) {
            try { return JSON.parse(localStorage.getItem(`f1_loc_${sessionKey}_${driverNumber || 'all'}`))?.data || []; } catch (e) { return []; }
        }
    },

    /**
     * 4. Analisi Sorpassi (Overtakes)
     * Fetches overtakes in a session.
     */
    async getOvertakes(sessionKey) {
        try {
            const cacheKey = `f1_ovtk_${sessionKey}`;
            return await fetchWithCache(`${BASE_URL}/overtakes?session_key=${sessionKey}`, cacheKey);
        } catch (error) {
            try { return JSON.parse(localStorage.getItem(`f1_ovtk_${sessionKey}`))?.data || []; } catch (e) { return []; }
        }
    },

    /**
     * 5. Dati Meteo Live (Weather)
     */
    async getWeather(sessionKey) {
        try {
            const cacheKey = `f1_wthr_${sessionKey}`;
            return await fetchWithCache(`${BASE_URL}/weather?session_key=${sessionKey}`, cacheKey);
        } catch (error) {
            try { return JSON.parse(localStorage.getItem(`f1_wthr_${sessionKey}`))?.data || []; } catch (e) { return []; }
        }
    },

    /**
     * 6. Analisi Strategia (Tyre Degradation)
     * Fetches stints for all drivers in a session.
     */
    async getStints(sessionKey) {
        try {
            const cacheKey = `f1_stnts_${sessionKey}`;
            return await fetchWithCache(`${BASE_URL}/stints?session_key=${sessionKey}`, cacheKey);
        } catch (error) {
            try { return JSON.parse(localStorage.getItem(`f1_stnts_${sessionKey}`))?.data || []; } catch (e) { return []; }
        }
    },

    /**
     * 7. Tempi sul Giro (Laps)
     * Fetches lap times for all drivers (or specific) in a session.
     */
    async getLaps(sessionKey, driverNumber = null) {
        try {
            let url = `${BASE_URL}/laps?session_key=${sessionKey}`;
            if (driverNumber) url += `&driver_number=${driverNumber}`;
            const cacheKey = `f1_laps_${sessionKey}_${driverNumber || 'all'}`;
            return await fetchWithCache(url, cacheKey);
        } catch (error) {
            try { return JSON.parse(localStorage.getItem(`f1_laps_${sessionKey}_${driverNumber || 'all'}`))?.data || []; } catch (e) { return []; }
        }
    },

    /**
     * 8. Terminale Direzione Gara (Race Control)
     * Fetches FIA official race control messages for a session.
     */
    async getRaceControl(sessionKey) {
        try {
            const cacheKey = `f1_rc_${sessionKey}`;
            return await fetchWithCache(`${BASE_URL}/race_control?session_key=${sessionKey}`, cacheKey);
        } catch (error) {
            try { return JSON.parse(localStorage.getItem(`f1_rc_${sessionKey}`))?.data || []; } catch (e) { return []; }
        }
    },

    /**
     * 9. Analisi Mini-Settori e "Driver at Risk" (Timing Tower)
     * Fetches intervals (gap to leader and car ahead) in a session.
     */
    async getIntervals(sessionKey) {
        try {
            const cacheKey = `f1_int_${sessionKey}`;
            return await fetchWithCache(`${BASE_URL}/intervals?session_key=${sessionKey}`, cacheKey);
        } catch (error) {
            try { return JSON.parse(localStorage.getItem(`f1_int_${sessionKey}`))?.data || []; } catch (e) { return []; }
        }
    }
};
